<template>
  <div class="results-container" :class="selectionMode ? ['selection-mode'] : []">
    <div
      v-if="ingredients.length === 0"
      class="no-results-text has-text-centered is-italic"
      :class="{ 'is-indeterminate': isLoading }"
    >
      <p>{{ noResultsText || DEFAULT_NO_RESULTS_TEXT }}</p>
      <p>
        <a @click.prevent="emit('clear-search')">{{ clearSearchText }}</a>
      </p>
    </div>

    <table
      v-if="ingredients.length > 0"
      :class="(tableClass + ' ingredient-list table is-striped is-fullwidth').trim()"
    >
      <thead :class="tableHeadClass">
        <tr>
          <th
            v-if="selectionMode"
            class="col-checkbox no-print"
            :class="{ 'highlight-checkbox-pulse': showingSelectionPrompt }"
          >
            <a
              href="#"
              class="plus-button"
              title="Add results to selection"
              @click.prevent="handleSelectAllVisible(true)"
            >
              <span class="icon">
                <i class="far fa-plus-square"></i>
              </span>
            </a>
            <a
              href="#"
              class="minus-button is-hidden-touch"
              title="Remove results from selection"
              @click.prevent="handleSelectAllVisible(false)"
            >
              <span class="icon">
                <i class="far fa-minus-square"></i>
              </span>
            </a>
          </th>
          <th v-if="replacementMode" style="width: 1px"></th>
          <th v-if="showIngredientKey"></th>
          <th width="1" :class="ingredientNameClass">
            <a v-if="enableClickToSort" @click.prevent="emit('sort-click', 'name')">Name</a>
            <span v-else>Name</span>
          </th>
          <th v-if="showTags" class="is-hidden-touch is-hidden-desktop-only">Tags</th>
          <th v-if="showUpdatedAt" class="col-modified is-hidden-touch is-hidden-desktop-only">
            <a v-if="enableClickToSort" @click.prevent="emit('sort-click', 'updated_at')">
              Modified
            </a>
            <span v-else>Modified</span>
          </th>
          <th v-if="showEmails" class="is-hidden-touch">User</th>
          <th v-if="!replacementMode" class="no-print"></th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="ingredient in ingredients"
          :id="`ingredient-row-${ingredient.id}`"
          :key="ingredient.id"
          :class="[
            { 'in-selection': selection.includes(ingredient.id) },
            { 'is-expanded': expanded[ingredient.id] },
          ]"
        >
          <td v-if="selectionMode" class="col-checkbox no-print">
            <label>
              <input
                type="checkbox"
                class="checkbox"
                :class="{ 'highlight-checkbox-pulse': showingSelectionPrompt }"
                :checked="isSelected(ingredient.id)"
                @change="toggleSelection(ingredient.id)"
              />
            </label>
          </td>
          <td v-if="replacementMode">
            <button
              class="button is-small has-text-info is-pulled-right"
              @click="emit('select-replacement', ingredient)"
            >
              Select
            </button>
          </td>
          <td v-if="showIngredientKey">
            <IngredientBadge
              v-if="ingredient?.badge"
              :caption="ingredient.badge?.caption"
              :icon="ingredient.badge?.icon"
            />
          </td>
          <td class="name">
            <a
              :href="ingredientPath(ingredient.id, { user_id: userId })"
              class="is-flex is-align-items-center is-flex-wrap-wrap"
              :target="openIngredientInNewTab || replacementMode ? '_blank' : null"
            >
              {{ ingredient.name }}

              <span v-if="ingredient.subrecipe" class="chip is-warning is-light">Subrecipe</span>
              <span v-if="ingredient.archived" class="chip archived-chip">Archived</span>
            </a>
            <div v-if="ingredient.safety" class="is-size-7 is-italic has-text-grey">
              <template v-if="ingredient.pass_fail_safety">
                {{ ingredient.safety_unit_label }}
                <span style="font-style: normal">&mdash; recorded per production run</span>
              </template>
              <template v-else-if="ingredient.safety_amount_display">
                {{ ingredient.safety_amount_display }} {{ ingredient.safety_unit_label }}
              </template>
              <template v-else>
                {{ ingredient.safety_unit_label }}
                <span style="font-style: normal">&mdash; target set per recipe</span>
              </template>
            </div>
            <span v-if="showUpdatedAt" class="is-italic updated-at-v is-size-7">
              updated {{ dateDisplay(ingredient.updated_at) }}
            </span>
          </td>
          <td v-if="showTags" class="col-tags">
            <span class="tag-list-v">
              <IngredientTagInput
                v-if="allowInlineTagEdits"
                :user-id="userId"
                :ingredient-id="String(ingredient.id)"
                :tags="ingredient.tags || []"
                :can-click="true"
                empty-caption=""
                @tag-click="(tag) => emit('tag-click', tag)"
              />
              <TagLabel
                v-for="tag in sortTags(ingredient.tags || [])"
                v-else
                :key="tag.id"
                :data-test-id="`tag-${tag.name}`"
                :tag-id="tag.id"
                :color="tag.color"
                :name="tag.name"
                :can-remove="false"
                :can-click="true"
                size="small"
                @tag-click="(tag) => emit('tag-click', tag)"
              />
            </span>
            <span
              v-if="showUpdatedAt"
              class="is-hidden-mobile is-hidden-widescreen is-italic is-size-7"
            >
              updated {{ dateDisplay(ingredient.updated_at) }}
            </span>
          </td>
          <td v-if="showUpdatedAt" class="col-modified is-hidden-touch is-hidden-desktop-only">
            {{ dateDisplay(ingredient.updated_at) }}
          </td>
          <td
            v-if="showEmails"
            class="is-hidden-touch"
            :title="`${ingredient.user_email} (${ingredient.user_id})`"
          >
            {{ truncate(ingredient.user_email) }}
          </td>
          <td v-if="!replacementMode" class="col-actions no-print">
            <div class="is-flex gap-2 is-justify-content-flex-end">
              <IngredientActionsButton
                data-test-id="ingredient-actions"
                :ingredient-id="ingredient.id"
                :user-id="userId"
                :archived="ingredient.archived"
                :can-archive="ingredient.can_archive"
                :archive-disabled-reason="ingredient.archive_disabled_reason"
                :ingredient-name="ingredient.name"
              />
            </div>

            <a
              class="expand-button-v is-hidden-tablet ml-2"
              data-expand-row
              @click.prevent="() => (expanded[ingredient.id] = !expanded[ingredient.id])"
            >
              <span class="icon">
                <i class="fa fa-fw fa-chevron-right"></i>
              </span>
            </a>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup>import { ref as _ref } from 'vue';

  import { sortBy } from "lodash-es";
  import { parseISO, formatRelative } from "date-fns";
  import { ingredientPath } from "routes";
  import IngredientActionsButton from "./ingredient-actions-button.vue";
  import IngredientBadge from "./ingredient-badge.vue";
  import IngredientTagInput from "./ingredient-tag-input.vue";
  import TagLabel from "./tag-label.vue";
  import { useSelectionPrompt } from "./lib/use-bulk-actions.js";

  const showingSelectionPrompt = useSelectionPrompt("ingredient-bulk-selection:hint");

  const DEFAULT_NO_RESULTS_TEXT = "No ingredients matched your search criteria.";

  const props = defineProps({
    ingredients: { type: Array, default: () => [] },
    userId: { type: [Number, String], required: true },
    replacementMode: { type: Boolean, default: false },
    showEmails: { type: Boolean, default: false },
    openIngredientInNewTab: { type: Boolean, default: false },
    tableClass: { type: String, default: "" },
    tableHeadClass: { type: String, default: "" },
    ingredientNameClass: { type: String, default: "ingredient-name" },
    isLoading: { type: Boolean, default: false },
    enableClickToSort: { type: Boolean, default: true },
    clearSearchText: { type: String, default: "Clear search" },
    noResultsText: { type: String, default: null },
    showUpdatedAt: { type: Boolean, default: true },
    showIngredientKey: { type: Boolean, default: false },
    showTags: { type: Boolean, default: false },
    allowInlineTagEdits: { type: Boolean, default: false },
    selectionMode: { type: Boolean, default: false },
    selection: { type: Array, default: () => [] },
  });

  const emit = defineEmits([
    "sort-click",
    "clear-search",
    "select-replacement",
    "tag-click",
    "toggle-selection",
    "select-all",
    "deselect-all",
  ]);

  const expanded = _ref({});

  const dateDisplay = (updatedAt) => {
    return formatRelative(parseISO(updatedAt), new Date());
  };

  const truncate = (s, maxChars = 32) => {
    if (!s) return s;
    return s.length > maxChars ? s.slice(0, maxChars) + "…" : s;
  };

  const sortTags = (tags) => sortBy(tags, (t) => t.name.toLowerCase());

  const isSelected = (ingredientId) => props.selection.includes(ingredientId);

  const toggleSelection = (ingredientId) => {
    emit("toggle-selection", ingredientId);
  };

  const handleSelectAllVisible = (isAdding) => {
    emit(isAdding ? "select-all" : "deselect-all");
  };
</script>

<style lang="scss" scoped>
  @import "~jose_modern/css";

  $col-checkbox-width: 5em;
  $col-checkbox-width-mobile: 2em;

  .col-checkbox {
    width: $col-checkbox-width;
  }

  td.col-checkbox {
    & > label {
      min-height: 1em;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 0.75em;
      cursor: pointer;
    }
    padding: 0; // padding is on the label/checkbox rather than the cell
  }

  th.col-checkbox {
    text-align: center;
  }

  .col-modified {
    white-space: nowrap;
  }

  .tag-list-v {
    display: flex;
    gap: 0.5em;
    align-items: center;
    flex-wrap: wrap;
  }

  .updated-at-v {
    display: none;
  }

  .expand-button-v {
    padding-top: 2px;
  }

  table {
    tbody tr td {
      vertical-align: middle;
    }
  }

  .is-indeterminate {
    opacity: 0;
    pointer-events: none;
  }

  @include desktop {
    th.ingredient-name {
      min-width: 60ch;
    }

    th.ingredient-name-within-modal {
      width: 100%;
      min-width: 50ch;
    }
  }

  @include desktop-only {
    tbody tr {
      border-top: 1px solid $grey-lighter;
    }
  }

  @include tablet-only {
    tbody tr {
      border-top: 1px solid $grey-lighter;
    }
  }

  @include until($widescreen) {
    table {
      & > thead > tr {
        display: flex;
        flex-direction: row;

        & > th:not(.col-checkbox) {
          flex-grow: 1;
        }
      }

      & > tbody {
        & > tr {
          display: flex;
          flex-direction: row;
          flex-wrap: wrap;
          align-items: center;

          & > td {
            border: none;
            flex-direction: row;
          }

          & > td.name {
            display: flex;
            flex-basis: 50%;
            flex-grow: 1;
            flex-direction: column;
          }

          & > td.col-actions {
            display: flex;
            order: 2;
          }

          & > td.col-tags {
            padding-top: 0;

            order: 3;
            flex-basis: 100%;
            display: flex;
            justify-content: space-between;

            .results-container.selection-mode & {
              margin-left: $col-checkbox-width;
            }
          }
        }
      }
    }
  }

  @include mobile {
    .col-checkbox {
      width: $col-checkbox-width-mobile;
    }

    // is-narrow (replacement modal) opts out of card mode; keep a separator.
    table.is-narrow tbody tr {
      border-top: 1px solid $grey-lighter;
    }

    table:not(.is-narrow) {
      margin-top: 0.5rem;

      & > thead {
        border: none;
      }

      & > th {
        border: none;
      }

      & > tbody {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;

        & > tr {
          border: 1px solid $grey-light;
          border-radius: $radius-large;
          background-color: transparent !important;

          & > td.col-checkbox {
            padding: 0;

            & > label {
              padding: 0.5em 0 0.5em 0.75em;
            }
          }
        }

        & > tr:not(.is-expanded) {
          & > td.col-tags {
            display: none;
          }
        }

        & > tr.is-expanded {
          [data-expand-row] {
            .icon {
              transform: rotate(90deg);
            }
          }

          .updated-at-v {
            display: block;
          }

          & > td.col-tags {
            display: flex;

            .results-container.selection-mode & {
              margin-left: $col-checkbox-width-mobile;
            }
          }
        }
      }
    }
  }

  // narrow: drop actions to its own row so the chevron can pin right
  @media screen and (max-width: 395px) {
    table:not(.is-narrow) > tbody > tr > td.col-actions {
      flex-basis: 100%;
      justify-content: space-between;
    }

    .results-container.selection-mode table:not(.is-narrow) > tbody > tr > td.col-actions {
      margin-left: $col-checkbox-width-mobile;
    }
  }

  @include tablet-only {
    th.ingredient-name,
    th.ingredient-name-within-modal {
      min-width: 50ch;
    }
  }

  @include until($tablet) {
    .table thead th {
      border: none;
      padding-top: 0;
    }
  }

  @include until($desktop) {
    th.col-modified {
      display: inline-flex;
      justify-content: flex-end;
    }

    // If table has is-narrow class, make it even smaller on non-desktop
    .table.is-narrow {
      font-size: 0.8rem;
    }
    .table.is-narrow th,
    .table.is-narrow td {
      padding: 0.25rem 0.5rem;
    }
    .table.is-narrow button {
      font-size: 0.75rem;
      padding: 0.2rem 0.4rem;
    }
  }

  .no-results-text {
    margin-top: 0.75rem;
    font-size: 1.25rem !important; // <-- is-size-5

    @include until($tablet) {
      font-size: 1rem !important; // <-- is-size-6
    }
  }

  @keyframes checkbox-highlight-pulse {
    0% {
      box-shadow: 0 0 0 0 rgba(50, 115, 220, 0.7);
      background-color: rgba(50, 115, 220, 0.1);
    }
    50% {
      box-shadow: 0 0 0 8px rgba(50, 115, 220, 0.3);
      background-color: rgba(50, 115, 220, 0.2);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(50, 115, 220, 0);
      background-color: transparent;
    }
  }

  .highlight-checkbox-pulse {
    animation: checkbox-highlight-pulse 1s ease-out;
    border-radius: 4px;
    transition: background-color 0.2s ease;
  }
</style>
